import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { SimpleSelectPrimitive } from "@/components/ui/simple-select";
import { ArrowLeft, ArrowRight, Palette, Info, X, FileText, Monitor } from "lucide-react";
import { Media } from "@/interfaces";
import { MediaServices } from "@/services/MediaServices";
import { ProjectFileUploader } from "@/utils/projectFileUploader";
import { getSupportedMimeType, URLS, useInterval, useSocialCoachSession } from "@/utils";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment/moment";

import "./styles.scss";
import { ControlOption } from "@/containers/RecordVideo/components/ControlOption";
import { useRecorderSettings } from "../RecordVideo/hooks/useRecorderSettings";
import { useVirtualBackground } from "../RecordVideo/hooks/useVirtualBackground";
import { VirtualBackgroundControl } from "../RecordVideo/components/VirtualBackgroundControl";
import { UploadingOverlay } from "../RecordVideo/components/UploadingOverlay";

interface FormSelectProps {
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string | undefined) => void;
  items: Array<{ label: string; value: string }>;
  disabled?: boolean;
}

const FormSelect: React.FC<FormSelectProps> = ({
  placeholder = "Select...",
  value,
  onValueChange,
  items,
  disabled,
}) => {
  return (
    <SimpleSelectPrimitive
      value={value}
      onValueChange={val => onValueChange && onValueChange(val || undefined)}
      disabled={disabled}
    >
      <SimpleSelectPrimitive.Trigger className="flex h-9 w-full items-center justify-between rounded-md border border-gray-200 bg-background px-3 py-2 text-sm placeholder:text-muted-foreground hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-2xs transition-colors">
        <SimpleSelectPrimitive.Value placeholder={placeholder} className="truncate pr-2">
          {items.find(i => i.value === value)?.label}
        </SimpleSelectPrimitive.Value>
      </SimpleSelectPrimitive.Trigger>

      <SimpleSelectPrimitive.Content align="start" className="w-full">
        {items.map(item => (
          <SimpleSelectPrimitive.Item key={item.value} value={item.value} className="text-sm py-1.5 px-3">
            {item.label}
          </SimpleSelectPrimitive.Item>
        ))}
      </SimpleSelectPrimitive.Content>
    </SimpleSelectPrimitive>
  );
};

type Props = {
  onSwitchToTeleprompter?: () => void;
  onCancel: () => void;
  onDone: (media: Media, secondsRecorded: number) => void;
};

const ScreenCaptureFC: React.FC<Props> = ({ onSwitchToTeleprompter, onCancel, onDone }) => {
  const history = useHistory();
  const location = useLocation();
  const { isCoach } = useSocialCoachSession();
  const urls = isCoach ? URLS.coach : URLS.player;

  const {
    bgType,
    setBgType,
    bgBlur,
    setBgBlur,
    bgColors,
    setBgColors,
    bgImage,
    setBgImage,
    bgLogo,
    setBgLogo,
    bgLogoStyle,
    setBgLogoStyle,
    hasPersistedSettings,
    debouncedBgBlur,
    debouncedBgColors,
  } = useRecorderSettings();

  // Recording State
  const [loading, setLoading] = useState<boolean>(false);
  const [recording, setRecording] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const recordingRef = useRef(false);
  const countdownRef = useRef<number | null>(null);

  const [loadingLabel, setLoadingLabel] = useState<string>("Uploading");
  const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined);

  const [displaySeconds, setDisplaySeconds] = useState(0);
  const recordedDurationMs = useRef(0);
  const segmentStartTimeRef = useRef(0);

  // Media State
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [recordedBlob, setRecordedBlob] = useState<Blob | undefined>(undefined);

  // PiP / Video Refs
  const pipVideoRef = useRef<HTMLVideoElement>(null);
  const hiddenSourceVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();

  // Refs for Capture
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [isPipActive, setIsPipActive] = useState(false);

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  // Microphone State
  const preferredMicId = localStorage.getItem("scRecorderPreferredMic");
  const [microphoneOptions, setMicrophoneOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [microphone, setMicrophone] = useState<string | undefined>(undefined);

  // Webcam State
  const preferredCamId = localStorage.getItem("scRecorderPreferredCam");
  const [webcamOptions, setWebcamOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedWebcam, setSelectedWebcam] = useState<string | undefined>(undefined);

  // Headless Virtual Background engine
  const {
    imageSegmenterRef,
    previousMaskDataRef,
    maskCanvasRef,
    offscreenCanvasRef,
    bgImageElementRef,
    bgLogoElementRef,
  } = useVirtualBackground(
    canvasRef,
    bgType,
    bgBlur,
    bgColors,
    bgImage,
    bgLogo,
    bgLogoStyle,
    debouncedBgBlur,
    debouncedBgColors,
    selectedWebcam,
    true,
    useRef(null), // Dummy webcamRef
    true // Headless mode!
  );

  // Sync refs for animation loop
  useEffect(() => {
    recordingRef.current = recording;
  }, [recording]);

  useEffect(() => {
    countdownRef.current = countdown;
  }, [countdown]);

  useEffect(() => {
    return () => {
      if (document.pictureInPictureElement && document.exitPictureInPicture) {
        document.exitPictureInPicture().catch(err => console.log("Failed to exit PiP:", err));
      }
    };
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (countdown !== null && countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => (prev !== null ? prev - 1 : null));
      }, 1000);
    } else if (countdown === 0) {
      setCountdown(null);
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "inactive") {
          mediaRecorderRef.current.start(1000);
          onStartRecording();
        }
      }, 100);
    }

    return () => clearInterval(interval);
  }, [countdown]);

  const mimeType = getSupportedMimeType();
  let videoType = mimeType.split(";")[0];
  const videoExt = videoType.split("/")[1];

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(setDevices);
  }, []);

  useEffect(() => {
    const audios = devices
      .filter(({ kind }) => kind === "audioinput")
      .map(element => ({
        label: element.label || `Microphone ${element.deviceId.slice(0, 5)}...`,
        value: element.deviceId,
      }));

    setMicrophoneOptions(audios);
    if (audios.length > 0 && !microphone) {
      const defaultMic = audios.find(element => element.value === preferredMicId) || audios[0];
      setMicrophone(`${defaultMic.value}`);
    }

    const availableCameras = devices
      .filter(({ kind }) => kind === "videoinput")
      .map(element => ({
        label: element.label || `Camera ${element.deviceId.slice(0, 5)}...`,
        value: element.deviceId,
      }));

    const allCameras = [...availableCameras, { label: "None", value: "none" }];
    setWebcamOptions(allCameras);

    if (allCameras.length > 1 && !selectedWebcam) {
      const defaultCam = allCameras.find(element => element.value === preferredCamId) || allCameras[0];
      setSelectedWebcam(`${defaultCam.value}`);
      startWebcamAndPip(`${defaultCam.value}`).catch(console.error);
    }
  }, [devices]);

  useInterval(() => {
    if (recording) {
      const elapsedSinceSegmentStart = Date.now() - segmentStartTimeRef.current;
      const totalElapsedMs = recordedDurationMs.current + elapsedSinceSegmentStart;
      const seconds = Math.floor(totalElapsedMs / 1000);
      setDisplaySeconds(seconds);
    }
  }, 1000);

  const drawOverlays = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, hasWebcam: boolean) => {
    if (countdownRef.current !== null && countdownRef.current > 0) {
      const countText = countdownRef.current.toString();
      const fontSize = Math.floor(canvas.width * 0.2);

      ctx.font = `bold ${fontSize}px Poppins, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.strokeStyle = "white";
      ctx.lineWidth = 15;
      ctx.lineJoin = "round";
      ctx.strokeText(countText, canvas.width / 2, canvas.height / 2);
    }

    if (recordingRef.current) {
      const elapsed = Date.now() - segmentStartTimeRef.current + recordedDurationMs.current;
      const totalSeconds = Math.floor(elapsed / 1000);
      const mm = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0");
      const ss = (totalSeconds % 60).toString().padStart(2, "0");
      const timeText = `${mm}:${ss}`;

      const fontSize = hasWebcam ? 48 : 140;
      const dotRadius = hasWebcam ? 12 : 35;
      const paddingH = hasWebcam ? 40 : 80;
      const paddingV = hasWebcam ? 20 : 60;
      const gap = hasWebcam ? 20 : 40;
      const marginBottom = hasWebcam ? 40 : 0;

      ctx.font = `bold ${fontSize}px Poppins, sans-serif`;
      ctx.textBaseline = "middle";
      ctx.textAlign = "start";

      const textMetrics = ctx.measureText(timeText);
      const textWidth = textMetrics.width;

      const pillWidth = paddingH + dotRadius * 2 + gap + textWidth + paddingH;
      const pillHeight = fontSize + paddingV;

      const pillX = (canvas.width - pillWidth) / 2;
      const pillY = hasWebcam ? canvas.height - pillHeight - marginBottom : (canvas.height - pillHeight) / 2;

      const contentCenterY = pillY + pillHeight / 2;

      ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
      ctx.shadowBlur = hasWebcam ? 15 : 30; // Bigger shadow for the big pill
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = hasWebcam ? 4 : 10;

      ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(pillX, pillY, pillWidth, pillHeight, 100);
      } else {
        ctx.rect(pillX, pillY, pillWidth, pillHeight);
      }
      ctx.fill();

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      const dotX = pillX + paddingH + dotRadius;
      const pulseAlpha = (Math.sin(Date.now() / 250) + 1) / 2;
      const minAlpha = 0.2;
      const currentAlpha = minAlpha + pulseAlpha * (1 - minAlpha);

      ctx.fillStyle = `rgba(255, 0, 0, ${currentAlpha})`;
      ctx.beginPath();
      ctx.arc(dotX, contentCenterY, dotRadius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.shadowColor = "rgba(0, 0, 0, 0.9)";
      ctx.shadowBlur = hasWebcam ? 4 : 8;
      ctx.shadowOffsetX = hasWebcam ? 1 : 2;
      ctx.shadowOffsetY = hasWebcam ? 1 : 2;

      ctx.fillStyle = "#FFFFFF";
      const textX = dotX + dotRadius + gap;
      ctx.fillText(timeText, textX, contentCenterY + (hasWebcam ? 4 : 10));

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }

    if (!recordingRef.current && (countdownRef.current === null || countdownRef.current === 0) && !hasWebcam) {
      const idleText = "Ready to record";

      ctx.font = "bold 100px Poppins, sans-serif";
      ctx.fillStyle = "#A0A0A0";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(idleText, canvas.width / 2, canvas.height / 2 - 20);
    }
  };

  const bgTypeRef = useRef(bgType);
  bgTypeRef.current = bgType;

  const bgBlurRef = useRef(bgBlur);
  bgBlurRef.current = bgBlur;

  const bgColorsRef = useRef(bgColors);
  bgColorsRef.current = bgColors;

  const bgImageRef = useRef(bgImage);
  bgImageRef.current = bgImage;

  const bgLogoRef = useRef(bgLogo);
  bgLogoRef.current = bgLogo;

  const bgLogoStyleRef = useRef(bgLogoStyle);
  bgLogoStyleRef.current = bgLogoStyle;

  useEffect(() => {
    bgLogoRef.current = bgLogo;
  }, [bgLogo]);

  useEffect(() => {
    bgLogoStyleRef.current = bgLogoStyle;
  }, [bgLogoStyle]);

  const renderCanvasLoop = () => {
    const source = hiddenSourceVideoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (canvas && ctx) {
      const hasWebcam = !!(source && source.srcObject && source.readyState >= 2);
      const segmenter = imageSegmenterRef.current;

      if (hasWebcam && bgTypeRef.current !== "none" && segmenter) {
        try {
          const timestamp = performance.now();
          segmenter.segmentForVideo(source, timestamp, result => {
            const masks = result.confidenceMasks;
            if (!masks || masks.length === 0) return;

            const width = canvas.width;
            const height = canvas.height;

            const mask = masks[0];
            const maskData = mask.getAsFloat32Array();
            const maskWidth = mask.width;
            const maskHeight = mask.height;

            if (!previousMaskDataRef.current || previousMaskDataRef.current.length !== maskData.length) {
              previousMaskDataRef.current = new Float32Array(maskData);
            }
            const prevMaskData = previousMaskDataRef.current;

            if (!maskCanvasRef.current) {
              maskCanvasRef.current = document.createElement("canvas");
            }
            const maskCanvas = maskCanvasRef.current;
            if (maskCanvas.width !== maskWidth || maskCanvas.height !== maskHeight) {
              maskCanvas.width = maskWidth;
              maskCanvas.height = maskHeight;
            }

            const maskCtx = maskCanvas.getContext("2d");
            if (!maskCtx) return;

            const maskImgData = maskCtx.createImageData(maskWidth, maskHeight);
            const pixels = maskImgData.data;

            const lerpAlpha = 0.35;
            const minThreshold = 0.25;
            const maxThreshold = 0.75;

            for (let i = 0; i < maskData.length; i++) {
              const currentConfidence = maskData[i];
              const smoothedConfidence = lerpAlpha * currentConfidence + (1 - lerpAlpha) * prevMaskData[i];
              prevMaskData[i] = smoothedConfidence;

              const normalized = Math.max(
                0,
                Math.min(1, (smoothedConfidence - minThreshold) / (maxThreshold - minThreshold))
              );
              const gatedConfidence = normalized * normalized * (3 - 2 * normalized);

              const pixelIndex = i * 4;
              pixels[pixelIndex] = 255;
              pixels[pixelIndex + 1] = 255;
              pixels[pixelIndex + 2] = 255;
              pixels[pixelIndex + 3] = Math.trunc(gatedConfidence * 255);
            }
            maskCtx.putImageData(maskImgData, 0, 0);

            if (!offscreenCanvasRef.current) {
              offscreenCanvasRef.current = document.createElement("canvas");
            }
            const offscreenCanvas = offscreenCanvasRef.current;
            if (offscreenCanvas.width !== width || offscreenCanvas.height !== height) {
              offscreenCanvas.width = width;
              offscreenCanvas.height = height;
            }

            const offCtx = offscreenCanvas.getContext("2d");
            if (!offCtx) return;

            offCtx.save();
            offCtx.clearRect(0, 0, width, height);
            offCtx.globalCompositeOperation = "copy";
            offCtx.filter = "blur(3px) contrast(1.5)";
            offCtx.drawImage(maskCanvas, 0, 0, width, height);
            offCtx.filter = "none";

            offCtx.globalCompositeOperation = "source-in";
            offCtx.drawImage(source, 0, 0, width, height);
            offCtx.restore();

            ctx.save();
            ctx.clearRect(0, 0, width, height);

            const currentBgType = bgTypeRef.current;
            if (currentBgType === "blur") {
              ctx.filter = `blur(${bgBlurRef.current}px)`;
              ctx.drawImage(source, 0, 0, width, height);
              ctx.filter = "none";
            } else if (currentBgType === "gradient") {
              const cx = width / 2;
              const cy = height * 0.35;
              const grad = ctx.createRadialGradient(cx, cy, 10, cx, cy, Math.max(width, height) * 0.8);

              const currentBgColors = bgColorsRef.current;
              if (currentBgColors.length === 1) {
                grad.addColorStop(0, currentBgColors[0]);
                grad.addColorStop(1, currentBgColors[0]);
              } else {
                currentBgColors.forEach((color, idx) => {
                  grad.addColorStop(idx / (currentBgColors.length - 1), color);
                });
              }

              ctx.fillStyle = grad;
              ctx.fillRect(0, 0, width, height);
            } else if (currentBgType === "image" && bgImageElementRef.current) {
              const img = bgImageElementRef.current;
              const imgRatio = img.width / img.height;
              const canvasRatio = width / height;
              let dWidth = width;
              let dHeight = height;
              let dx = 0;
              let dy = 0;
              if (imgRatio > canvasRatio) {
                dWidth = height * imgRatio;
                dx = (width - dWidth) / 2;
              } else {
                dHeight = width / imgRatio;
                dy = (height - dHeight) / 2;
              }
              ctx.drawImage(img, dx, dy, dWidth, dHeight);
            }

            // Draw Step-and-Repeat tiled logo behind subject if active
            if (bgLogoRef.current && bgLogoStyleRef.current === "repeat" && bgLogoElementRef.current) {
              const el = bgLogoElementRef.current;
              ctx.save();
              ctx.globalAlpha = 0.12;
              const logoH = 35;
              const logoW = (el.width / el.height) * logoH;
              const gapX = logoW * 1.5;
              const gapY = logoH * 2.5;
              for (let y = logoH; y < height; y += gapY) {
                const isOddRow = Math.trunc(y / gapY) % 2 === 1;
                const startX = isOddRow ? gapX / 2 : 0;
                for (let x = startX; x < width + logoW; x += gapX) {
                  ctx.drawImage(el, x - logoW / 2, y - logoH / 2, logoW, logoH);
                }
              }
              ctx.restore();
            }

            // Draw Backdrop Plaque Sign behind subject if active
            if (bgLogoRef.current && bgLogoStyleRef.current === "watermark" && bgLogoElementRef.current) {
              const el = bgLogoElementRef.current;
              const h = 60;
              const w = (el.width / el.height) * h;
              const margin = 40;
              const x = margin;
              const y = margin;
              ctx.save();
              ctx.fillStyle = "rgba(255, 255, 255, 0.15)";
              ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
              ctx.lineWidth = 1;
              ctx.beginPath();
              const padX = 20;
              const padY = 12;
              if (ctx.roundRect) {
                ctx.roundRect(x - padX, y - padY, w + padX * 2, h + padY * 2, 8);
              } else {
                ctx.rect(x - padX, y - padY, w + padX * 2, h + padY * 2);
              }
              ctx.fill();
              ctx.stroke();
              ctx.globalAlpha = 0.9;
              ctx.drawImage(el, x, y, w, h);
              ctx.restore();
            }

            ctx.globalCompositeOperation = "source-over";
            ctx.drawImage(offscreenCanvas, 0, 0, width, height);

            ctx.restore();

            drawOverlays(ctx, canvas, hasWebcam);
          });
        } catch (err) {
          console.error("Error running selfie segmenter in ScreenCapture:", err);
          // Fallback if segmenter crashes
          ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
          drawOverlays(ctx, canvas, hasWebcam);
        }
      } else {
        if (hasWebcam) {
          ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
        } else {
          ctx.fillStyle = "#1e1e1e";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        drawOverlays(ctx, canvas, hasWebcam);
      }
    }

    animationFrameRef.current = requestAnimationFrame(renderCanvasLoop);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (webcamStreamRef.current) {
        webcamStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [previewUrl]);

  const startWebcamAndPip = async (deviceId: string) => {
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach(track => track.stop());
      webcamStreamRef.current = null;
    }

    if (hiddenSourceVideoRef.current) {
      hiddenSourceVideoRef.current.srcObject = null;
    }

    let hasWebcam = false;

    if (deviceId !== "none") {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: deviceId },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        webcamStreamRef.current = stream;

        if (hiddenSourceVideoRef.current) {
          hiddenSourceVideoRef.current.srcObject = stream;
          await hiddenSourceVideoRef.current.play();
          hasWebcam = true;
        }
      } catch (error) {
        console.error("Failed to start Webcam, falling back to timer-only mode:", error);
      }
    }

    if (canvasRef.current && pipVideoRef.current) {
      canvasRef.current.width =
        hasWebcam && hiddenSourceVideoRef.current ? hiddenSourceVideoRef.current.videoWidth : 1280;
      canvasRef.current.height =
        hasWebcam && hiddenSourceVideoRef.current ? hiddenSourceVideoRef.current.videoHeight : 720;

      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      renderCanvasLoop();

      if (!pipVideoRef.current.srcObject) {
        pipVideoRef.current.srcObject = canvasRef.current.captureStream(30);
      }

      try {
        await pipVideoRef.current.play();

        if (document.pictureInPictureEnabled && !document.pictureInPictureElement) {
          await pipVideoRef.current.requestPictureInPicture();
        }
      } catch (e) {
        console.error("PiP failed or was blocked:", e);
      }

      pipVideoRef.current.addEventListener("enterpictureinpicture", () => setIsPipActive(true));
      pipVideoRef.current.addEventListener("leavepictureinpicture", e => {
        setIsPipActive(false);
      });
    }
  };

  const handleWebcamChange = (deviceId: string) => {
    setSelectedWebcam(deviceId);
    localStorage.setItem("scRecorderPreferredCam", deviceId);
    startWebcamAndPip(deviceId).catch(console.error);
  };

  const onStartRecording = () => {
    recordedDurationMs.current = 0;
    setDisplaySeconds(0);
    segmentStartTimeRef.current = Date.now();
    setPreviewUrl(undefined);
    setRecordedBlob(undefined);
    setRecording(true);
  };

  const startScreenCapture = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(console.error);
    }

    let audioContext: AudioContext | null = null;
    try {
      if (selectedWebcam && (!document.pictureInPictureElement || pipVideoRef.current?.paused)) {
        await startWebcamAndPip(selectedWebcam);
      }

      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor",
        },
        audio: false,
      });

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: microphone ? { exact: microphone } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
        },
        video: false,
      });

      audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      if (micStream.getAudioTracks().length > 0) {
        const micSource = audioContext.createMediaStreamSource(micStream);
        micSource.connect(destination);
      }

      if (displayStream.getAudioTracks().length > 0) {
        const sysSource = audioContext.createMediaStreamSource(displayStream);
        sysSource.connect(destination);
      }

      const mixedAudioTrack = destination.stream.getAudioTracks()[0];
      const combinedStream = new MediaStream([...displayStream.getVideoTracks(), mixedAudioTrack]);

      streamRef.current = combinedStream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(combinedStream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = e => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      displayStream.getVideoTracks()[0].onended = () => {
        stopScreenCapture();
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: videoType });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setPreviewUrl(url);
        setRecording(false);
        setCountdown(null);

        if (document.visibilityState === "hidden" && Notification.permission === "granted") {
          const notification = new Notification("SocialCoach Video Ready", {
            body: "Your screen capture is processed and ready to view.",
          });
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }

        if (document.pictureInPictureElement && document.exitPictureInPicture) {
          document.exitPictureInPicture().catch(err => console.log(err));
        }
        if (webcamStreamRef.current) {
          webcamStreamRef.current.getTracks().forEach(t => t.stop());
          webcamStreamRef.current = null;
        }
        if (hiddenSourceVideoRef.current) {
          hiddenSourceVideoRef.current.srcObject = null;
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        const segmentDuration = Date.now() - segmentStartTimeRef.current;
        recordedDurationMs.current += segmentDuration;

        combinedStream.getTracks().forEach(track => track.stop());
        micStream.getTracks().forEach(track => track.stop());
        displayStream.getTracks().forEach(track => track.stop());
        audioContext?.close();
      };

      setCountdown(5);
    } catch (err) {
      audioContext?.close();
      console.error("Error starting screen capture:", err);
      setRecording(false);
    }
  };

  const stopScreenCapture = () => {
    setCountdown(null);

    console.log("Stopping screen capture", mediaRecorderRef.current?.state);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    } else {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setRecording(false);
    }
  };

  const handleReRecord = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(undefined);
    setRecordedBlob(undefined);
  };

  const handleUploadAndNext = async () => {
    if (!recordedBlob) return;

    const uploader = new ProjectFileUploader("screenCapture");
    let ext = videoExt;
    setLoading(true);
    setLoadingProgress(0);
    setLoadingLabel("Uploading");

    const media: Media = {
      id: 0,
      sortOrder: 0,
      category: "VIDEO",
      fileName: `screenCapture_${new Date().getTime()}`,
      uri: previewUrl || "",
      type: videoType,
      extension: ext,
      imageFile: recordedBlob,
    };

    try {
      const uploaded = await uploader.prepareMedia([media], (_, progress) => {
        setLoadingProgress(Math.trunc(progress * 100));
      });

      console.log("Uploaded media:", uploaded);
      if (ext === "webm") {
        transcode(uploaded[0]).then(media => {
          onDone(media, displaySeconds);
          setLoading(false);
        });
      } else {
        onDone(uploaded[0], displaySeconds);
        setLoading(false);
      }
    } catch (e) {
      console.error("Upload failed", e);
      setLoading(false);
    }
  };

  const transcode = async (media: Media) => {
    try {
      setLoadingProgress(undefined);
      setLoadingLabel("Processing media. This may take a minute...");
      let job = await MediaServices.transcode({ mediaUrl: media.uri });
      const jobId = job.jobName;
      while (job.jobStatus !== "SUCCEEDED") {
        await new Promise(r => setTimeout(r, 2000));
        job = await MediaServices.getJobStatus(jobId);
      }

      return {
        ...media,
        uri: job.downloadLink,
        extension: "mp4",
        type: "video/mp4",
      } as Media;
    } catch (e) {
      console.error("Error transcoding:", e);
      throw e;
    }
  };

  return (
    <div className="w-full flex flex-col gap-6 relative pb-32">
      {countdown !== null && (
        <div className="countdownTimer">
          <span className="countdownText">{countdown}</span>
        </div>
      )}

      <video
        ref={hiddenSourceVideoRef}
        playsInline
        muted
        autoPlay
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      />

      <canvas ref={canvasRef} style={{ position: "absolute", top: -9999, left: -9999 }} />

      <div
        style={
          !!previewUrl
            ? { display: "none" }
            : isPipActive
            ? { position: "absolute", top: "-9999px", left: "-9999px" }
            : { position: "fixed", width: "300px", right: "20px", bottom: "20px", zIndex: 30 }
        }
      >
        <video
          ref={pipVideoRef}
          muted
          autoPlay
          playsInline
          style={{
            width: "100%",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Full-width Header Panel */}
      <div className="flex w-full justify-between items-center p-4 border-b border-gray-100 select-none bg-background">
        <div className="w-1/4 text-left pl-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="font-semibold text-gray-500 hover:text-gray-900 hover:bg-gray-100/80 rounded-lg px-3.5 h-9 flex items-center justify-center leading-none"
          >
            <ArrowLeft className="size-4 mr-2 shrink-0" /> Back
          </Button>
        </div>

        <div className="w-1/2 flex items-center justify-center">
          <span className="text-sm font-semibold text-slate-600 dark:text-slate-400 select-none">
            Screen Capture - {moment.utc(displaySeconds * 1000).format("mm:ss")}
          </span>
        </div>

        <div className="w-1/4 flex items-center justify-end gap-2 text-right pr-2">
          {!recording && !previewUrl && (
            <Button
              variant="default"
              onClick={startScreenCapture}
              disabled={countdown !== null}
              className="font-bold shadow-2xs h-9 text-sm flex items-center justify-center px-4 cursor-pointer"
            >
              Start Screen Capture
            </Button>
          )}
          {recording && (
            <Button
              variant="destructive"
              onClick={stopScreenCapture}
              className="font-bold shadow-2xs h-9 text-sm flex items-center justify-center px-4 cursor-pointer"
            >
              Stop Recording
            </Button>
          )}
          {!recording && previewUrl && (
            <Button variant="secondary" onClick={handleReRecord} className="font-semibold shadow-3xs h-9 text-sm">
              Re-record
            </Button>
          )}
          {!recording && previewUrl && (
            <Button
              variant="default"
              onClick={handleUploadAndNext}
              className="font-bold shadow-2xs h-9 text-sm flex items-center justify-center px-4 cursor-pointer"
            >
              Next <ArrowRight className="size-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Main Central Container: Constrains all inputs, and buttons so they never stretch infinitely on wide screens */}
      <div className="w-full max-w-2xl mx-auto flex flex-col gap-6 px-4">
        {/* Input Selectors Grid */}
        {!previewUrl && (
          <div className="w-full grid grid-cols-2 gap-6 mt-1 text-left animate-in fade-in duration-200">
            {/* Microphone Selector */}
            <div>
              <ControlOption title={"Microphone"}>
                <FormSelect
                  placeholder="Select microphone..."
                  onValueChange={val => {
                    if (val) {
                      setMicrophone(val);
                      localStorage.setItem("scRecorderPreferredMic", val);
                    }
                  }}
                  items={microphoneOptions}
                  value={microphone}
                />
              </ControlOption>
            </div>

            {/* Webcam Selector */}
            <div>
              <ControlOption title={"Webcam Overlay"}>
                <div className="flex flex-col gap-2">
                  <FormSelect
                    placeholder="Select webcam..."
                    onValueChange={val => val && handleWebcamChange(val)}
                    items={webcamOptions}
                    value={selectedWebcam}
                  />

                  {selectedWebcam !== "none" && (
                    <div className="flex items-center gap-3 select-none text-xs font-semibold mt-1 pl-0.5 leading-none">
                      <button
                        type="button"
                        onClick={() => handleWebcamChange("none")}
                        className="text-gray-500 hover:text-gray-900 underline cursor-pointer bg-transparent border-0 flex items-center gap-1.5 leading-none p-0"
                      >
                        <X className="size-3.5 shrink-0" />
                        Turn off camera
                      </button>
                    </div>
                  )}
                </div>
              </ControlOption>
            </div>
          </div>
        )}

        {/* Settings Drawer for Virtual Background */}
        {!previewUrl && selectedWebcam !== "none" && (
          <div className="w-full animate-in slide-in-from-top-2 fade-in duration-200 mt-1">
            <div className="w-full rounded-xl border border-gray-200 bg-gray-50/50 p-5 shadow-3xs">
              <VirtualBackgroundControl
                aspectRatio={16 / 9}
                bgType={bgType}
                bgBlur={bgBlur}
                bgColors={bgColors}
                bgImage={bgImage}
                bgLogo={bgLogo}
                onBgLogoChange={setBgLogo}
                bgLogoStyle={bgLogoStyle}
                onBgLogoStyleChange={setBgLogoStyle}
                onBgTypeChange={setBgType}
                onBgBlurChange={setBgBlur}
                onBgColorsChange={setBgColors}
                onBgImageChange={setBgImage}
              />
            </div>
          </div>
        )}

        {/* Info Card Message (Naturally pushed down by above elements) */}
        {!recording && !previewUrl && selectedWebcam !== "none" && (
          <div className="w-full select-none mt-1 animate-in fade-in duration-200">
            <div className="w-full rounded-xl border border-blue-200/60 bg-blue-50/50 p-4 flex gap-3 shadow-3xs items-start">
              <Info className="size-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1 text-sm text-blue-900/90 leading-relaxed text-left">
                <span className="font-bold tracking-tight">Want to include your webcam?</span>
                <span>
                  Make sure to choose <strong>Entire Screen</strong> when your browser asks what to share. If you select
                  a specific window or tab, your camera overlay won't be captured in the final SocialCoach video.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Main Trigger Actions / Start Button below Infobox */}
        {!recording && !previewUrl && (
          <div className="w-full flex justify-center mt-4 animate-in fade-in duration-200">
            <Button
              variant="default"
              onClick={startScreenCapture}
              disabled={countdown !== null}
              className="font-bold shadow-md px-10 py-6 text-lg rounded-xl transition-all active:scale-95 cursor-pointer"
            >
              Start Screen Capture
            </Button>
          </div>
        )}

        {/* Main Preview Player */}
        {!recording && previewUrl && (
          <div className="w-full flex justify-center pb-8 animate-in zoom-in-95 duration-200 mt-2">
            <video
              src={previewUrl}
              controls
              className="w-full max-h-[420px] rounded-lg shadow-lg border border-gray-200 bg-black"
            />
          </div>
        )}
      </div>

      {/* Navigation Footer (Floating, centered, absolute solid opaque background with high z-index) */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 bg-white dark:bg-zinc-900 border border-gray-200/80 rounded-full p-1.5 shadow-xl flex gap-1 select-none animate-in fade-in duration-200">
        <Button
          size="sm"
          variant="ghost"
          className="font-semibold text-gray-500 hover:text-gray-900 h-8 cursor-pointer rounded-full"
          disabled={recording}
          onClick={() => {
            if (
              !recording &&
              previewUrl &&
              !window.confirm("Switching modes will clear your current progress. Are you sure?")
            ) {
              return;
            }
            stopScreenCapture();
            if (onSwitchToTeleprompter) {
              onSwitchToTeleprompter();
            } else {
              history.replace(urls.recordVideo, location.state);
            }
          }}
        >
          <FileText className="size-4 mr-2" /> Teleprompter
        </Button>
        <Button
          size="sm"
          className="font-semibold bg-teal-500/10 hover:bg-teal-500/20 text-teal-600 border-0 h-8 cursor-default rounded-full"
        >
          <Monitor className="size-4 mr-2" /> Screen Record
        </Button>
      </div>

      {/* Uploading Overlay */}
      <UploadingOverlay loading={loading} loadingLabel={loadingLabel} loadingProgress={loadingProgress} />
    </div>
  );
};

export const ScreenCapture = ScreenCaptureFC;
